import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Zap, Moon, Sun, Menu, X } from "lucide-react";
import { useTheme } from "../lib/theme";
import AuthModal from "./AuthModal";
import UserAccountMenu from "./UserAccountMenu";
import { OPEN_AUTH_MODAL_EVENT, type OpenAuthModalDetail } from "../lib/openAuthModal";
import type { SessionUser } from "../lib/sessionUser";
import { readCachedSessionUser, writeCachedSessionUser } from "../lib/sessionCache";
import { trackAuth } from "../analytics";
import { setSentryUser, clearSentryUser } from "../sentry";

const TopHeader = () => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState<"signin" | "signup">("signin");
  const [authErrorCode, setAuthErrorCode] = useState<string | null>(null);
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(() => readCachedSessionUser());
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const SESSION_TRACKED_KEY = "analytics_session_tracked";

  const refreshSession = async () => {
    try {
      const response = await fetch("/api/auth/session");
      if (!response.ok) return;
      const session = (await response.json()) as { user?: SessionUser } | null;
      const user = session?.user || null;
      setSessionUser(user);
      writeCachedSessionUser(user);
      if (user?.email) {
        window.dispatchEvent(new Event("auth-session-updated"));
        setSentryUser(user.email);
        // Track sign-in only once per browser session (survives SPA navigations)
        const alreadyTracked = sessionStorage.getItem(SESSION_TRACKED_KEY);
        if (alreadyTracked !== user.email) {
          sessionStorage.setItem(SESSION_TRACKED_KEY, user.email);
          trackAuth("sign_in", user.email);
        }
      } else {
        clearSentryUser();
      }
    } catch {
      setSessionUser(null);
      writeCachedSessionUser(null);
    }
  };

  useEffect(() => {
    void refreshSession();
  }, []);

  useEffect(() => {
    const onSession = () => void refreshSession();
    window.addEventListener("auth-session-updated", onSession);
    return () => window.removeEventListener("auth-session-updated", onSession);
  }, []);

  useEffect(() => {
    const openAuthModal = (event: Event) => {
      const detail = (event as CustomEvent<OpenAuthModalDetail>).detail;
      setAuthModalMode(detail?.mode ?? "signup");
      setAuthErrorCode(null);
      setIsAuthModalOpen(true);
    };
    window.addEventListener(OPEN_AUTH_MODAL_EVENT, openAuthModal);
    return () => window.removeEventListener(OPEN_AUTH_MODAL_EVENT, openAuthModal);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get("authError") || params.get("error");
    if (!authError) return;
    setAuthErrorCode(authError);
    setIsAuthModalOpen(true);
    params.delete("authError");
    params.delete("error");
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ""}`;
    window.history.replaceState({}, "", nextUrl);
  }, []);

  const handleSignOut = () => {
    void (async () => {
      try {
        const csrfRes = await fetch("/api/auth/csrf");
        const csrfJson = (await csrfRes.json()) as { csrfToken?: string };
        const csrfToken = csrfJson.csrfToken;
        if (!csrfToken) return;
        const form = new URLSearchParams();
        form.set("csrfToken", csrfToken);
        form.set("callbackUrl", window.location.href);
        await fetch("/api/auth/signout", {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: form.toString(),
        });
        window.location.reload();
      } catch {
        // ignore transient signout errors
      }
    })();
  };

  return (
    <>
      <motion.header
        initial={{ y: -4, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className={`sticky top-0 z-[100] w-full transition-all duration-300 ${
          scrolled
            ? "glass border-b border-border/60 shadow-sm"
            : "bg-background/80 backdrop-blur-sm border-b border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-6 h-16 flex items-center justify-between gap-4">
          {/* ── Logo ── */}
          <Link
            to="/"
            className="flex items-center gap-2.5 group flex-shrink-0"
            aria-label="API to MCP home"
          >
            <div className="w-8 h-8 rounded-xl bg-primary flex items-center justify-center shadow-sm group-hover:shadow-md group-hover:scale-105 transition-all duration-200">
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="text-[0.9375rem] font-bold tracking-tight text-foreground">
              API <span className="text-primary">→</span> MCP
            </span>
          </Link>

          {/* ── Desktop nav ── */}
          <nav className="hidden md:flex items-center gap-1 text-sm font-medium text-muted-foreground">
            <Link
              to="/registry"
              className="px-3 py-1.5 rounded-lg hover:text-foreground hover:bg-accent transition-colors"
            >
              Registry
            </Link>
            <a
              href="/#try-now"
              className="px-3 py-1.5 rounded-lg hover:text-foreground hover:bg-accent transition-colors"
            >
              Convert
            </a>
            <Link
              to="/pricing"
              className="px-3 py-1.5 rounded-lg hover:text-foreground hover:bg-accent transition-colors"
            >
              Pricing
            </Link>
            <Link
              to="/blog"
              className="px-3 py-1.5 rounded-lg hover:text-foreground hover:bg-accent transition-colors"
            >
              Blog
            </Link>
          </nav>

          {/* ── Right controls ── */}
          <div className="flex items-center gap-2">
            {/* Dark mode toggle */}
            <motion.button
              type="button"
              onClick={toggleTheme}
              whileHover={{ scale: 1.08 }}
              whileTap={{ scale: 0.93 }}
              aria-label="Toggle theme"
              className="w-9 h-9 flex items-center justify-center rounded-xl border border-border bg-background hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
            >
              <AnimatePresence mode="wait" initial={false}>
                {theme === "dark" ? (
                  <motion.span
                    key="sun"
                    initial={{ rotate: -60, opacity: 0, scale: 0.6 }}
                    animate={{ rotate: 0, opacity: 1, scale: 1 }}
                    exit={{ rotate: 60, opacity: 0, scale: 0.6 }}
                    transition={{ duration: 0.2 }}
                    className="flex"
                  >
                    <Sun className="w-4 h-4" />
                  </motion.span>
                ) : (
                  <motion.span
                    key="moon"
                    initial={{ rotate: 60, opacity: 0, scale: 0.6 }}
                    animate={{ rotate: 0, opacity: 1, scale: 1 }}
                    exit={{ rotate: -60, opacity: 0, scale: 0.6 }}
                    transition={{ duration: 0.2 }}
                    className="flex"
                  >
                    <Moon className="w-4 h-4" />
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.button>

            {/* Sign in / user menu */}
            {sessionUser ? (
              <UserAccountMenu user={sessionUser} onSignOut={handleSignOut} />
            ) : (
              <motion.button
                type="button"
                onClick={() => {
                  setAuthModalMode("signin");
                  setIsAuthModalOpen(true);
                }}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
                className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-xl btn-neu-primary transition-all"
              >
                Sign In
              </motion.button>
            )}

            {/* Mobile burger */}
            <button
              type="button"
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-xl border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* ── Mobile menu ── */}
        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22 }}
              className="md:hidden overflow-hidden border-t border-border/50"
            >
              <div className="px-5 py-3 flex flex-col gap-1 text-sm font-medium">
                <Link
                  to="/registry"
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-2.5 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                >
                  Registry
                </Link>
                <a
                  href="/#try-now"
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-2.5 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                >
                  Convert
                </a>
                <Link
                  to="/pricing"
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-2.5 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                >
                  Pricing
                </Link>
                <Link
                  to="/blog"
                  onClick={() => setMobileOpen(false)}
                  className="px-3 py-2.5 rounded-lg hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                >
                  Blog
                </Link>
                {!sessionUser && (
                  <button
                    type="button"
                    onClick={() => {
                      setMobileOpen(false);
                      setAuthModalMode("signin");
                      setIsAuthModalOpen(true);
                    }}
                    className="mt-1 w-full py-2.5 rounded-xl btn-neu-primary text-sm"
                  >
                    Sign In
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>

      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => {
          setIsAuthModalOpen(false);
          setAuthErrorCode(null);
        }}
        initialAuthError={authErrorCode}
        initialMode={authModalMode}
        onAuthenticated={() => void refreshSession()}
      />
    </>
  );
};

export default TopHeader;
